// POST /organizations/{organizationId}/payment-methods (#375 / DecisionLog §1.6).
// Capturing a card is what activates a newly approved membership — approval itself
// no longer charges.

// Which kind of payment a checkout selection carries (#296/#297). The SINGLE declaration
// of this enum — @models/order re-exports this one rather than declaring its own, so an
// API-side renumbering lands in exactly one place and admin and customer surfaces can
// never drift into two values that fail each other's `===`.
//
// CONFIRMED against API main @ 386f687 (PR 242),
// PurchasingPlatform.Api/Domain/Types/PaymentMethodType.cs — member-for-member. Numeric:
// this API serializes enums as integers in both directions.
export enum PaymentMethodType {
    None = 0,
    Card = 1,
    BankAccount = 2,
}

export interface CreatePaymentMethodRequest {
    // The Accept.js nonce as "<dataDescriptor>|<dataValue>" — exactly what
    // useAcceptUi hands back in AcceptUiCapture.token. No PAN/CVV ever reaches
    // our code or the API (PCI scope).
    paymentToken: string;
}

// What the card save did beyond storing the card (#381, audit MB-07). Mirrors the
// API's CreatePaymentMethodOutcomeType member-for-member; numeric on the wire. None
// is never returned. Renewed wins when one save both renewed and activated. It
// reports what HAPPENED, not permission: access still comes from getMeUser.
export enum CreatePaymentMethodOutcome {
    None = 0,
    CardStored = 1,
    MembershipActivated = 2,
    MembershipRenewed = 3,
}

export interface CreatePaymentMethodResponse {
    // Both are best-effort: the vendor's CIM create call returns no masked PAN,
    // so either can be null on a 200. Render defensively.
    cardBrand: string | null;
    cardLast4: string | null;
    // Optional so a response without it (an API before #381) still reads as a
    // plain card save rather than as anything about the membership.
    outcome?: CreatePaymentMethodOutcome;
}

// The 402 body is the shared { error, message } failure envelope — see
// ApiFailureBody in @utils/apiFailure. `message` is customer-safe copy authored by
// the API, and it is the only thing that distinguishes the two payment_declined
// variants ("we couldn't save that card" vs "your card WAS saved but the charge was
// declined"), so callers render it whenever it is present and fall back to their
// own copy only when it is blank.

// Definitively no money moved. The only code safe to offer a retry on.
export const PAYMENT_DECLINED = "payment_declined";

// The charge outcome is unknown and money MAY have moved, so the API deliberately
// stopped and will not retry that location. Never render a retry control for this.
export const PAYMENT_UNCONFIRMED = "payment_unconfirmed";

// The vendor is holding a REAL transaction for review (arrives on checkout's
// POST /orders 402, not this endpoint); the server message carries the transaction
// id the customer must quote to support. A retry would charge again on top of the
// hold — never invite one.
export const PAYMENT_HELD = "payment_held";

// MB-05 (#439): the payment gateway refused OUR request (merchant account or
// gateway configuration), so nothing was charged and the card was never judged.
// Safe to retry once the gateway works. Arrives on POST /orders (checkout), the
// returned-payment fee and POST /organizations/{id}/payment-methods — on the last
// only AFTER the card was stored, from the membership charge that follows.
export const PAYMENT_UNAVAILABLE = "payment_unavailable";

// Not a payment problem at all: the card is stored and fine, but a local precondition
// blocked activation. No card fixes it, so this must never read as a card rejection.
export const ACTIVATION_UNAVAILABLE = "activation_unavailable";
